import { useCallback, useEffect, useRef, useState } from "react";

interface ConnectionStatus {
  isConnected: boolean;
  lastPing: Date | null;
  reconnectAttempts: number;
  isReconnecting: boolean;
}

interface ConnectionMonitorOptions {
  pingInterval?: number; // How often to check for pings (default: 35 seconds)
  maxReconnectAttempts?: number; // Max reconnection attempts (default: 5)
  reconnectDelay?: number; // Delay between reconnection attempts (default: 2000ms)
  onConnectionLost?: () => void;
  onConnectionRestored?: () => void;
  onMaxReconnectAttemptsReached?: () => void;
}

export const useConnectionMonitor = (options: ConnectionMonitorOptions = {}) => {
  const {
    pingInterval = 35000, // 35 seconds (slightly longer than server's 30s ping)
    maxReconnectAttempts = 5,
    reconnectDelay = 2000,
    onConnectionLost,
    onConnectionRestored,
    onMaxReconnectAttemptsReached,
  } = options;

  const [status, setStatus] = useState<ConnectionStatus>({
    isConnected: false,
    lastPing: null,
    reconnectAttempts: 0,
    isReconnecting: false,
  });

  const pingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPingTimeRef = useRef<number>(0);

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Handle ping received from server
  const handlePing = useCallback(() => {
    const now = Date.now();
    lastPingTimeRef.current = now;

    setStatus((prev) => ({
      ...prev,
      isConnected: true,
      lastPing: new Date(now),
      reconnectAttempts: 0, // Reset on successful ping
      isReconnecting: false,
    }));

    // Clear any existing ping timeout
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
    }

    // Set new timeout to check for next ping
    pingTimeoutRef.current = setTimeout(() => {
      setStatus((prev) => {
        if (prev.isConnected) {
          console.warn("ConnectionMonitor: No ping received, connection may be lost");
          onConnectionLost?.();
          return {
            ...prev,
            isConnected: false,
            isReconnecting: true,
          };
        }
        return prev;
      });
    }, pingInterval);

    // Call connection restored callback if we were reconnecting
    if (status.isReconnecting) {
      onConnectionRestored?.();
    }
  }, [pingInterval, onConnectionLost, onConnectionRestored, status.isReconnecting]);

  // Handle connection established
  const handleConnectionOpen = useCallback(() => {
    console.log("ConnectionMonitor: Connection opened");
    setStatus((prev) => ({
      ...prev,
      isConnected: true,
      reconnectAttempts: 0,
      isReconnecting: false,
    }));
    handlePing(); // Start monitoring
  }, [handlePing]);

  // Handle connection error
  const handleConnectionError = useCallback(() => {
    console.warn("ConnectionMonitor: Connection error detected");
    setStatus((prev) => ({
      ...prev,
      isConnected: false,
      isReconnecting: true,
    }));
    onConnectionLost?.();
  }, [onConnectionLost]);

  // Attempt to reconnect
  const attemptReconnect = useCallback(() => {
    setStatus((prev) => {
      if (prev.reconnectAttempts >= maxReconnectAttempts) {
        console.error("ConnectionMonitor: Max reconnection attempts reached");
        onMaxReconnectAttemptsReached?.();
        return {
          ...prev,
          isReconnecting: false,
        };
      }

      console.log(`ConnectionMonitor: Attempting reconnection ${prev.reconnectAttempts + 1}/${maxReconnectAttempts}`);

      // Schedule next reconnection attempt
      reconnectTimeoutRef.current = setTimeout(() => {
        setStatus((current) => ({
          ...current,
          reconnectAttempts: current.reconnectAttempts + 1,
        }));
      }, reconnectDelay);

      return {
        ...prev,
        reconnectAttempts: prev.reconnectAttempts + 1,
      };
    });
  }, [maxReconnectAttempts, reconnectDelay, onMaxReconnectAttemptsReached]);

  // Auto-reconnect when connection is lost
  useEffect(() => {
    if (status.isReconnecting && status.reconnectAttempts > 0 && status.reconnectAttempts <= maxReconnectAttempts) {
      attemptReconnect();
    }
  }, [status.isReconnecting, status.reconnectAttempts, maxReconnectAttempts, attemptReconnect]);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    setStatus((prev) => ({
      ...prev,
      reconnectAttempts: 0,
      isReconnecting: true,
    }));
  }, []);

  // Reset connection status
  const reset = useCallback(() => {
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    setStatus({
      isConnected: false,
      lastPing: null,
      reconnectAttempts: 0,
      isReconnecting: false,
    });
  }, []);

  return {
    status,
    handlePing,
    handleConnectionOpen,
    handleConnectionError,
    reconnect,
    reset,
  };
};
